<!-- Блок ПЛАН/ФАКТ показывает сохранённую калькуляцию и расходы конкретного заказа. -->
<template>
  <section class="card order-economics" aria-label="План и факт по заказу">
    <base-loader v-if="calculationStore.isLoading" />

    <template v-else>
      <PlanCalculationSwitchNotice
        v-if="calculatorSwitchNotice"
        :calculator-name="calculatorSwitchNotice.calculatorName"
        :result-name="calculatorSwitchNotice.resultName"
        :version="calculatorSwitchNotice.version"
        :previous-version="calculatorSwitchNotice.previousPlan.version"
        :is-calculating="calculationStore.isCalculating"
        @repeat-values="repeatPreviousValues"
        @close="calculatorSwitchNotice = null"
      />

      <PlanCalculationAttemptWarning
        v-if="latestAttemptFailed"
        :error="latestError"
        :has-current-plan="Boolean(currentPlan)"
        :calculator-preset-id="calculationStore.latestAttempt?.calculator_preset_id"
        :calculator-name="calculationStore.latestAttempt?.preset_snapshot?.name"
        :can-configure-calculator="canConfigureCalculator"
      />

      <div class="order-economics__columns">
        <div class="order-economics__column order-economics__column--plan">
          <PlanCalculationPlanSummary
            :current-plan="currentPlan"
            :summary-items="summaryItems"
            :result-name="resultName"
            :result-unit="resultUnit"
            :result-tone="resultTone"
            :expanded="showDetails"
            :calculate-disabled="!canUpdateOrder"
            :is-calculating="calculationStore.isCalculating"
            :presets="presetsStore.presets"
            :default-preset-id="defaultPresetId"
            :changed-count="planChangedCount"
            :can-calculate="canUpdateOrder"
            :can-switch-calculator="canSelectCalculator"
            :can-configure-calculator="canConfigureCalculator"
            :calculator-options-loading="calculatorOptionsLoading"
            @toggle="showDetails = !showDetails"
            @recalculate="recalculate"
            @switch-calculator="switchCalculator"
            @request-calculators="loadCalculatorOptions"
          />

          <div v-if="showDetails" class="order-economics__details">
            <PlanCalculationPanel
              v-if="currentPlan"
              :current-plan="currentPlan"
              :calculations="calculationStore.calculations"
              :history-count="calculationStore.planHistoryCount"
              :has-more-history="calculationStore.hasMorePlanHistory"
              :is-loading-history="calculationStore.isLoadingPlanHistory"
              :result-name="resultName"
              :result-unit="resultUnit"
              :result-tone="resultTone"
              :is-calculating="calculationStore.isCalculating"
              :editable="canUpdateOrder"
              @recalculate="recalculate"
              @load-more-history="loadMorePlanHistory"
              @changed-count="draftChangedCount = $event"
            />

            <PlanCalculationEmptyPanel
              v-else
              :can-calculate="canUpdateOrder"
              :is-calculating="calculationStore.isCalculating"
              @calculate="recalculate"
            />
          </div>
        </div>

        <div class="order-economics__column order-economics__column--fact">
          <PlanCalculationFactSummary
            :expanded="showDetails"
            :can-view-expenses="canViewExpenses"
            :status-label="factStatusLabel"
            :status-tone="factStatusTone"
            :profit="calculationStore.factProfit"
            :mileage="calculationStore.factMileageKm"
            :profit-per-km="calculationStore.factProfitPerKm"
            :can-create-expense="canCreateExpenses"
            @toggle="showDetails = !showDetails"
            @add-expense="showExpenseModal = true"
          />

          <div v-if="showDetails" class="order-economics__details">
            <PlanCalculationFactPanel
              :total="calculationStore.factTotal"
              :order-price="calculationStore.factOrderPrice"
              :profit="calculationStore.factProfit"
              :mileage="calculationStore.factMileageKm"
              :telemetry-mileage="calculationStore.factTelemetryMileageKm"
              :planned-mileage="plannedMileage"
              :profit-per-km="calculationStore.factProfitPerKm"
              :planned-profit-per-km="plannedProfitPerKm"
              :mileage-overridden="calculationStore.factMileageOverridden"
              :mileage-updated-by="calculationStore.factMileageUpdatedBy"
              :mileage-updated-at="calculationStore.factMileageUpdatedAt"
              :telemetry-updated-at="calculationStore.factTelemetryCalculatedAt"
              :mileage-refresh-pending="calculationStore.factMileageRefreshPending"
              :is-mileage-submitting="calculationStore.isFactSubmitting"
              :expense-count="calculationStore.factExpenseCount"
              :expense-count-label="factExpenseCountLabel"
              :categories="calculationStore.expenseCategories"
              :latest-expenses="calculationStore.latestExpenses"
              :is-loading="calculationStore.isFactLoading"
              :can-view-expenses="canViewExpenses"
              :can-edit-mileage="canUpdateOrder"
              @edit-expense="editExpense"
              @open-expenses="openExpenses"
              @save-mileage="saveFactMileage"
              @reset-mileage="saveFactMileage(null)"
              @refresh-mileage="refreshFactMileage"
            />
          </div>
        </div>
      </div>
    </template>

    <AddExpensesModal
      v-if="showExpenseModal"
      :trip-id="expenseTripId"
      :order-id="orderId"
      @close="showExpenseModal = false"
      @expense-created="handleExpenseCreated"
    />

    <EditExpensesModal
      v-if="selectedExpenseId"
      :expense-id="selectedExpenseId"
      @close="selectedExpenseId = null"
      @expense-updated="handleExpenseChanged"
      @expense-deleted="handleExpenseChanged"
      @expense-accepted="handleExpenseChanged"
    />
  </section>
</template>

<script>
import AddExpensesModal from "@/components/Expense/AddExpensesModal.vue";
import EditExpensesModal from "@/components/Expense/EditExpensesModal.vue";
import PlanCalculationAttemptWarning from "@/components/Order/PlanCalculation/AttemptWarning.vue";
import PlanCalculationEmptyPanel from "@/components/Order/PlanCalculation/EmptyPanel.vue";
import PlanCalculationFactPanel from "@/components/Order/PlanCalculation/FactPanel.vue";
import PlanCalculationFactSummary from "@/components/Order/PlanCalculation/FactSummary.vue";
import PlanCalculationPanel from "@/components/Order/PlanCalculation/Panel.vue";
import PlanCalculationPlanSummary from "@/components/Order/PlanCalculation/PlanSummary.vue";
import PlanCalculationSwitchNotice from "@/components/Order/PlanCalculation/SwitchNotice.vue";
import { useCalculatorPresetsStore } from "@/stores/calculatorPresetsStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useOrderCalculationStore } from "@/stores/orderCalculationStore";
import { useOrderCalculationSettingsStore } from "@/stores/orderCalculationSettingsStore";
import { can } from "@/utils/permissionUtils";
import { formatRussianCount } from "@/utils/pluralizationUtils";
import { buildCalculationRepeatPayload } from "@/utils/orderCalculation/overrideUtils";
import { findPlanSemanticValue } from "@/utils/orderCalculation/factPresentationUtils";

export default {
  name: "OrderPlanCalculation",

  components: {
    AddExpensesModal,
    EditExpensesModal,
    PlanCalculationAttemptWarning,
    PlanCalculationEmptyPanel,
    PlanCalculationFactPanel,
    PlanCalculationFactSummary,
    PlanCalculationPanel,
    PlanCalculationPlanSummary,
    PlanCalculationSwitchNotice,
  },

  props: {
    orderId: { type: [String, Number], required: true },
    trips: { type: Array, default: () => [] },
    expenseRevision: { type: Number, default: 0 },
  },

  data() {
    return {
      selectedPresetId: null,
      showDetails: false,
      showExpenseModal: false,
      selectedExpenseId: null,
      draftChangedCount: null,
      calculatorSwitchNotice: null,
      factRefreshTimer: null,
      calculatorOptionsLoaded: false,
      calculatorOptionsLoading: false,
    };
  },

  computed: {
    calculationStore() {
      return useOrderCalculationStore();
    },
    presetsStore() {
      return useCalculatorPresetsStore();
    },
    calculationSettingsStore() {
      return useOrderCalculationSettingsStore();
    },
    notificationStore() {
      return useNotificationStore();
    },
    currentPlan() {
      return this.calculationStore.currentPlan;
    },
    defaultPresetId() {
      return this.calculationSettingsStore.setting
        ?.default_calculator_preset_id || null;
    },
    latestAttemptFailed() {
      return this.calculationStore.latestAttempt?.status === "failed";
    },
    latestError() {
      return this.calculationStore.latestAttempt?.error_details?.join(", ") || "Причина не указана.";
    },
    resultSnapshot() {
      return this.currentPlan?.preset_snapshot || {};
    },
    resultName() {
      return this.resultSnapshot.result_name || "Результат";
    },
    resultUnit() {
      return "rub";
    },
    resultTone() {
      const result = Number(this.currentPlan?.result);
      if (result < 0) return "negative";
      if (result > 0) {
        return "positive";
      }
      return "neutral";
    },
    summaryItems() {
      return this.calculationStore.planSummaryItems;
    },
    planChangedCount() {
      if (this.draftChangedCount !== null) return this.draftChangedCount;
      return Object.keys(this.currentPlan?.input_overrides || {}).length;
    },
    canViewExpenses() {
      return can("Expense", "index");
    },
    canCreateExpenses() {
      return can("Expense", "create");
    },
    canUpdateOrder() {
      return can("Order", "update");
    },
    canSelectCalculator() {
      return this.canUpdateOrder && can("Calculator::Preset", "index");
    },
    canConfigureCalculator() {
      return can("Calculator::Preset", "show")
        && can("Calculator::Preset", "update");
    },
    activeTrips() {
      return this.trips.filter(
        (trip) => trip.statusCode !== "canceled"
      );
    },
    hasTrips() {
      return this.activeTrips.length > 0;
    },
    expenseTripId() {
      return this.activeTrips.length === 1 ? Number(this.activeTrips[0].id) : null;
    },
    factExpenseCountLabel() {
      return this.formatCount(
        this.calculationStore.factExpenseCount,
        "запись",
        "записи",
        "записей"
      );
    },
    plannedMileage() {
      return findPlanSemanticValue(this.currentPlan, "mileage_km");
    },
    plannedProfitPerKm() {
      return findPlanSemanticValue(this.currentPlan, "profit_per_km");
    },
    factStatusLabel() {
      if (!this.canViewExpenses) return "Расходы недоступны";
      if (!this.hasTrips) return "Рейс ещё не создан";
      if (this.activeTrips.length > 1) {
        return this.formatCount(this.activeTrips.length, "рейс", "рейса", "рейсов");
      }
      return this.activeTrips[0].statusLabel || "Статус не указан";
    },
    factStatusTone() {
      if (!this.hasTrips || !this.canViewExpenses) return "muted";
      const tones = {
        planned: "planned",
        in_progress: "progress",
        completed: "completed",
        draft: "muted",
      };
      return tones[this.activeTrips[0]?.statusCode] || "muted";
    },
  },

  watch: {
    orderId() {
      this.clearFactRefreshTimer();
      this.calculatorSwitchNotice = null;
      this.draftChangedCount = null;
      this.load();
    },
    trips: {
      handler() {
        this.loadFact();
      },
      deep: true,
    },
    expenseRevision() {
      this.loadFact();
    },
  },

  methods: {
    // Просмотр заказа не зависит от доступа к административным настройкам калькуляторов
    async load() {
      try {
        await this.calculationStore.fetchPlan(this.orderId);
        this.selectedPresetId = this.currentPlan?.calculator_preset_id || null;
      } catch {
        // Ошибку API уже показывает общий обработчик; заказ должен остаться доступным.
      }
    },
    // Список нужен только для явной смены калькулятора, а не для просмотра ПЛАНа
    async loadCalculatorOptions() {
      if (
        !this.canSelectCalculator
        || this.calculatorOptionsLoaded
        || this.calculatorOptionsLoading
      ) {
        return;
      }

      this.calculatorOptionsLoading = true;
      try {
        await Promise.all([
          this.presetsStore.fetchEnabledPresets(),
          this.calculationSettingsStore.fetchSetting({ includePresets: false }),
        ]);
        this.calculatorOptionsLoaded = true;
      } catch {
        // Ошибка дополнительных настроек не скрывает сохранённый ПЛАН заказа.
      } finally {
        this.calculatorOptionsLoading = false;
      }
    },
    // Загружает ФАКТ по явной связи расходов с заказом.
    async loadFact() {
      if (!this.canViewExpenses) {
        this.calculationStore.fact = null;
        return;
      }
      try {
        await this.calculationStore.fetchFact(this.orderId);
        this.scheduleFactRefreshPoll();
      } catch {
        // Общий обработчик покажет ошибку, а блок ФАКТа останется без недостоверных сумм.
      }
    },

    async loadMorePlanHistory() {
      try {
        await this.calculationStore.loadMorePlanHistory(this.orderId);
      } catch {
        // Общий обработчик API покажет ошибку, уже загруженная история сохранится.
      }
    },

    // Создаёт новую версию ПЛАНа, не изменяя сохранённые версии вручную на клиенте.
    async createPlanVersion(
      { presetId = this.selectedPresetId, presetParams = [] } = {},
      successMessage = "ПЛАН заказа пересчитан"
    ) {
      try {
        const calculation = await this.calculationStore.recalculate(
          this.orderId,
          presetId,
          presetParams
        );
        if (!calculation) return null;
        if (calculation.status === "failed") {
          this.notificationStore.showError("ПЛАН не рассчитан. Проверьте данные заказа и калькулятор");
          return null;
        }
        this.selectedPresetId = calculation.calculator_preset_id;
        this.draftChangedCount = null;
        if (successMessage) this.notificationStore.showSuccess(successMessage);
        return calculation;
      } catch {
        // Сетевая или серверная ошибка уже показана общим обработчиком API.
        return null;
      }
    },

    async recalculate(payload = {}) {
      await this.createPlanVersion(payload);
    },

    // Смена калькулятора всегда создаёт новую версию и не переносит значения старого.
    async switchCalculator(presetId) {
      const previousPlan = this.currentPlan;
      const calculation = await this.createPlanVersion(
        { presetId, presetParams: [] },
        null
      );
      if (!calculation) return;

      this.calculatorSwitchNotice = {
        previousPlan,
        calculatorName: calculation.preset_snapshot?.name || "Калькулятор",
        resultName: calculation.preset_snapshot?.result_name || "Результат",
        version: Number(calculation.version),
      };
    },

    // Повторяет ручные значения версии, но рассчитывает их по актуальному пресету
    async repeatPreviousValues() {
      const previousPlan = this.calculatorSwitchNotice?.previousPlan;
      if (!previousPlan) return;

      const calculation = await this.createPlanVersion(
        buildCalculationRepeatPayload(previousPlan),
        `Значения версии v${previousPlan.version} повторены в новом расчёте`
      );
      if (calculation) this.calculatorSwitchNotice = null;
    },

    // После создания расхода перечитывает ФАКТ, чтобы сумма и категории сразу обновились.
    async handleExpenseCreated() {
      this.showExpenseModal = false;
      await this.loadFact();
    },

    editExpense(expense) {
      this.selectedExpenseId = expense.id;
    },

    async handleExpenseChanged() {
      this.selectedExpenseId = null;
      await this.loadFact();
    },

    async saveFactMileage(value) {
      try {
        await this.calculationStore.updateFactMileage(this.orderId, value);
        this.notificationStore.showSuccess(
          value === null ? "Ручное значение пробега сброшено" : "Фактический пробег сохранён"
        );
      } catch {
        // Ошибку API уже показывает общий обработчик; текущий ФАКТ остаётся на экране.
      }
    },

    async refreshFactMileage() {
      try {
        const refreshResult = await this.calculationStore.refreshFactMileage(this.orderId);
        const message = refreshResult.refresh_status === "already_requested"
          ? "Обновление пробега уже запущено"
          : "Обновление пробега запущено";
        this.notificationStore.showSuccess(message);
        this.scheduleFactRefreshPoll();
      } catch {
        // Ошибку API уже показывает общий обработчик; сохранённый ФАКТ не скрываем.
      }
    },

    scheduleFactRefreshPoll() {
      this.clearFactRefreshTimer();
      if (!this.calculationStore.factMileageRefreshPending) return;

      const orderId = String(this.orderId);
      this.factRefreshTimer = window.setTimeout(async () => {
        if (String(this.orderId) !== orderId) return;
        try {
          await this.calculationStore.fetchFact(this.orderId, { silent: true });
        } catch {
          // Не повторяем ошибочный запрос циклически: глобальный обработчик уже показал ошибку.
          return;
        }
        this.scheduleFactRefreshPoll();
      }, 2000);
    },

    clearFactRefreshTimer() {
      if (this.factRefreshTimer === null) return;
      window.clearTimeout(this.factRefreshTimer);
      this.factRefreshTimer = null;
    },

    openExpenses() {
      this.$router.push({
        name: "OrderDetails",
        params: { id: this.orderId },
        query: { tab: "expenses" },
      });
    },

    formatCount(value, one, few, many) {
      return formatRussianCount(value, { one, few, many });
    },
  },

  created() {
    this.load();
    this.loadFact();
  },

  beforeUnmount() {
    this.clearFactRefreshTimer();
  },
};
</script>

<style scoped>
.order-economics {
  gap: 18px;
  margin-bottom: var(--base-out-indent);
  padding: 18px;
}

.order-economics__columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.order-economics__column {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 18px;
  /* Сводки должны реагировать на выделенную колонке ширину, а не на ширину окна. */
  container-type: inline-size;
}

@media (max-width: 1199px) {
  .order-economics__columns {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
